/**
 * Detect OS and architecture for Silly Dragon download.
 *
 * Detection priority:
 *   1. navigator.userAgentData.architecture (Chromium — most reliable)
 *   2. WebGL renderer string (Apple GPU → arm64, Intel → x64)
 *   3. navigator.cpuClass / userAgent heuristics
 *
 * Downloads are fetched from the public silly-dragon-releases repo.
 * The latest release is resolved automatically via the GitHub API.
 * Set REACT_APP_SILLY_DRAGON_RELEASES_BASE to override (e.g. a CDN URL).
 */

const RELEASES_REPO = 'Chinese-Community-AI/silly-dragon-releases';
const FALLBACK_VERSION = '0.2.1';
const CUSTOM_BASE =
  typeof process !== 'undefined' && process.env?.REACT_APP_SILLY_DRAGON_RELEASES_BASE !== undefined
    ? process.env.REACT_APP_SILLY_DRAGON_RELEASES_BASE
    : '';

let _cachedRelease = null;

/**
 * Fetch the latest release info from GitHub (cached for the session).
 * Returns { version, baseUrl, assets[] }.
 */
export async function fetchLatestRelease() {
  if (_cachedRelease) return _cachedRelease;

  if (CUSTOM_BASE) {
    _cachedRelease = { version: FALLBACK_VERSION, baseUrl: CUSTOM_BASE.replace(/\/$/, ''), assets: [] };
    return _cachedRelease;
  }

  try {
    const res = await fetch(`https://api.github.com/repos/${RELEASES_REPO}/releases/latest`);
    if (!res.ok) throw new Error(res.statusText);
    const data = await res.json();
    const tag = data.tag_name || '';
    const version = tag.replace(/^v/, '') || FALLBACK_VERSION;
    const baseUrl = `https://github.com/${RELEASES_REPO}/releases/download/${tag}`;
    const assets = (data.assets || []).map((a) => a.name);
    _cachedRelease = { version, baseUrl, assets };
  } catch (_) {
    const baseUrl = `https://github.com/${RELEASES_REPO}/releases/download/v${FALLBACK_VERSION}`;
    _cachedRelease = { version: FALLBACK_VERSION, baseUrl, assets: [] };
  }

  return _cachedRelease;
}

function detectMacArch() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      if (dbg) {
        const renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '';
        if (/Apple M|Apple GPU/i.test(renderer)) return 'arm64';
        if (/Intel|AMD|Radeon/i.test(renderer)) return 'x64';
      }
    }
  } catch (_) { /* WebGL unavailable */ }
  if (!/Intel/.test(navigator.userAgent || '')) return 'arm64';
  return 'arm64';
}

function detectWinArch(ua) {
  if (/arm64|aarch64/i.test(ua)) return 'arm64';
  return 'x64';
}

export function getDownloadPlatform() {
  if (typeof navigator === 'undefined')
    return { os: 'other', arch: 'x64', labelKey: 'deviceOther' };

  const ua = navigator.userAgent || '';
  const platform = (navigator.platform || '').toLowerCase();
  const isMac = /mac|ipad|iphone|ipod/.test(platform) || /Macintosh|Mac OS/i.test(ua);
  const isWin = /win/.test(platform) || /Windows/i.test(ua);

  let arch = 'x64';
  if (navigator.userAgentData?.architecture) {
    arch = navigator.userAgentData.architecture === 'arm' ? 'arm64' : 'x64';
  } else if (isMac) {
    arch = detectMacArch();
  } else if (isWin) {
    arch = detectWinArch(ua);
  }

  if (isMac) {
    const labelKey = arch === 'arm64' ? 'deviceMacAppleSilicon' : 'deviceMacIntel';
    return { os: 'mac', arch, labelKey, ext: 'dmg', platformSlug: `mac-${arch}` };
  }
  if (isWin) {
    const labelKey = arch === 'arm64' ? 'deviceWindowsArm64' : 'deviceWindows64';
    const platformSlug = arch === 'arm64' ? 'win-arm64' : 'win-x64';
    return { os: 'windows', arch, labelKey, ext: 'exe', platformSlug };
  }
  return { os: 'other', arch, labelKey: 'deviceOther' };
}

/**
 * Build the filename + download URL using the latest release info.
 * Must be called after fetchLatestRelease() resolves.
 */
export function buildDownloadInfo(platformInfo, release) {
  if (!platformInfo.platformSlug || !release) return { filename: null, url: null };
  const filename = `Silly-Dragon-${release.version}-${platformInfo.platformSlug}.${platformInfo.ext}`;
  const url = `${release.baseUrl}/${filename}`;
  return { filename, url };
}
